import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import DocumentRef from "./DocumentRef";
import { discountShortfall } from "./Order";
import COLORS from "../colors";
import {
  deepFreeze,
  formatCurrency,
  formatPO,
  isDue,
  roundPrice,
  timestampToDate,
} from "../util/misc";
import { i18n } from "../boot/i18n";
import { cloneDeep, every, get, isEmpty, isNumber, isString } from "lodash";
import { diff } from "deep-object-diff";
import { parse } from "date-fns";

export const BILL_FIELDS = {
  orderPO: "",
  ackID: "",
  invoiceID: null,
  vendorID: null,
  vendorInvoiceNumber: "",
  invoiceDate: null,
  dueDate: null,
  totalCost: null,
  balance: 0,
  creditMemo: false,
  document: null,
  missingDocument: false,
  reason: "",
  locked: false,

  // Meta info
  created: null,
  createdBy: null,
  modified: null,
  modifiedBy: null,
};
deepFreeze(BILL_FIELDS);

export const BILL_STATUS_TYPES = {
  unpaid: {
    order: 1,
    label: "Due",
    icon: "due_date",
    color: "warning",
  },
  "near-due": {
    order: 2,
    label: "Due",
    icon: "due_date",
    color: "negative",
  },
  due: {
    order: 3,
    label: "Due",
    icon: "due_date",
    color: "negative",
  },
  paid: {
    order: 4,
    label: "Paid",
    icon: "paid",
    color: "positive",
  },
  credit: {
    order: 5,
    label: "Credit",
    icon: "credit_memo",
    color: "positive",
  },
};
deepFreeze(BILL_STATUS_TYPES);

export default class Bill extends DataStructure {
  constructor(projectID, saleID, orderID, id, data = {}) {
    // Backward compatibility
    if (!data.orderPO) {
      data.orderPO = projectID + saleID;
    }
    if (!("balance" in data)) {
      data = { ...data, balance: data.totalCost };
    }

    data = sanitize(data, BILL_FIELDS);

    // Convert to Date
    data.invoiceDate = timestampToDate(data.invoiceDate);
    data.dueDate = timestampToDate(data.dueDate);
    data.created = timestampToDate(data.created);
    data.modified = timestampToDate(data.modified);

    super(data);

    this.id = id || null;
    this.projectID = projectID || null;
    this.saleID = saleID || null;
    this.orderID = orderID || null;
    this.permissions = Object.freeze({
      edit: this.canBeEditedBy,
      delete: this.canBeDeletedBy,
      unlock: this.canBeUnlockedBy,
    });

    // Documents
    if (this.data.document) {
      this.data.document = this.document.data;
    }
  }

  // Permissions

  canBeEditedBy(user) {
    const sale = this.sale;
    return (
      sale &&
      !sale.locked &&
      (!this.id || user.isAdmin || !this.locked) &&
      user.isEmployee
    );
  }

  canBeDeletedBy(user) {
    const sale = this.sale;
    return (
      sale &&
      !sale.locked &&
      !this.locked &&
      // Bills with any payment applied must be reversed via the payments
      // ledger first (deleting a paid bill leaves stranded `tx` records
      // pointing at a non-existent bill and double-counts the vendor
      // balance — see DELETE_BILL discussion in BUSINESS_LOGIC).
      !this.hasPayments &&
      user.isEmployee &&
      (user.id === this.data.createdBy || user.isAdmin)
    );
  }

  canBeUnlockedBy(user) {
    return user.isAdmin;
  }

  // Database

  get path() {
    return Bill.getPath(this.projectID, this.saleID, this.orderID, this.id);
  }

  static getPath(projectID, saleID, orderID, id) {
    let path = `projects/${projectID}/sales/${saleID}/orders/${orderID}/bills`;
    if (id) {
      path += `/${id}`;
    }
    return path;
  }

  static toFirestore(bill) {
    return bill.data;
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    return new Bill(
      snapshot.ref.parent.parent.parent.parent.parent.parent.id,
      snapshot.ref.parent.parent.parent.parent.id,
      snapshot.ref.parent.parent.id,
      snapshot.id,
      data
    );
  }

  // Editing

  get rules() {
    const isCredit = this.data.creditMemo;
    return {
      ackID: [(a) => Boolean(a) || "required"],
      vendorID: [(a) => Boolean(a) || "required"],
      vendorInvoiceNumber: [(a) => Boolean(a) || "required"],
      invoiceDate: [(a) => Boolean(a) || "required"],
      dueDate: [(a) => Boolean(a) || isCredit || "required"],
      totalCost: [
        (a) =>
          (isNumber(a) && (isCredit ? a < 0 : a >= 0)) || "totalCostInvalid",
      ],
      document: [
        (a) =>
          Boolean(this.data.missingDocument || (a && this.document.isValid)) ||
          "validDocumentRequired",
      ],
      reason: [
        (a) =>
          Boolean(!this.data.missingDocument || a.trim().length) ||
          "reasonRequiredMissingDocument",
      ],
    };
  }

  get isCreditMemo() {
    return Boolean(this.data.creditMemo);
  }

  // True when any payment (or credit-memo application) has reduced this
  // bill's balance below its original total cost. Used to gate deletion —
  // partially or fully paid bills can't simply be deleted because the
  // outgoing `tx` payment records (locked by transactions.js) would be
  // orphaned, leaving the vendor balance and `tx` ledger out of sync.
  get hasPayments() {
    const totalCost = Number(this.data.totalCost) || 0;
    const balance = Number(this.data.balance) || 0;
    return roundPrice(Math.abs(totalCost - balance)) > 0;
  }

  get isFinalBill() {
    const sale = this.sale;
    return this.id && sale
      ? sale.status.acks[this.data.ackID] &&
          sale.status.acks[this.data.ackID].fullyBilled
      : null;
  }

  // Compares the billed cost against the order's discounted target when an
  // additional discount was expected. `flag` is true when the discount is not
  // clearly reflected in the bill. Credit memos are excluded (their negative
  // totals are not a billed product cost).
  get discountReview() {
    if (this.isCreditMemo) {
      return null;
    }
    const order = this.order;
    if (!order || !order.expectsAdditionalDiscount) {
      return null;
    }
    const target = order.discountedCostTarget;
    const actual = this.data.totalCost;
    if (!isNumber(actual) || target === null) {
      return null;
    }
    return {
      flag: discountShortfall(actual, target),
      target,
      actual,
      percent: order.data.additionalDiscountPercent,
    };
  }

  get isValid() {
    const data = Bill.sanitize(cloneDeep(this.data));
    return every(this.rules, (rules, key) =>
      rules.every((rule) => rule(get(data, key)) === true)
    );
  }

  setDueDate() {
    let vendor = this.vendor;
    if (!vendor) {
      return;
    }
    let terms = vendor.paymentTerms;
    if (!terms) {
      return;
    }
    if (this.data.invoiceDate) {
      this.data.dueDate = terms.getDueDate(this.data.invoiceDate);
    } else {
      this.data.dueDate = "";
    }
  }

  getAvailableDiscount(date) {
    if (isString(date)) {
      date = parse(date, i18n.global.t("format.date-standard"), new Date());
    }
    let vendor = this.vendor;
    if (!vendor) {
      return 0;
    }
    let terms = vendor.paymentTerms;
    if (!terms) {
      return 0;
    }
    if (terms.discount && terms.discountDays) {
      let discountDate = terms.getDiscountDate(this.data.invoiceDate);
      if (date < discountDate) {
        return roundPrice(this.data.balance * (terms.discount / 100));
      }
    }
    return 0;
  }

  get availableDiscount() {
    return this.getAvailableDiscount(new Date());
  }

  get isEmpty() {
    return (
      !this.id &&
      isEmpty(
        diff(
          Bill.sanitize({ ...this.data, ackID: "", vendorID: null }),
          BILL_FIELDS
        )
      )
    );
  }

  isEqual(bill) {
    return (
      this.id === bill.id &&
      isEmpty(
        diff(
          Bill.sanitize(cloneDeep(this.data)),
          Bill.sanitize(cloneDeep(bill.data))
        )
      )
    );
  }

  static cleanDocuments(data, removeUnsaved = false) {
    if (data.document) {
      const doc = new DocumentRef(data.document);
      if (!doc[removeUnsaved ? "isSaved" : "isValid"]) {
        data.document = null;
      }
    }
    return data;
  }

  static sanitize(data, removeUnsaved) {
    if (data.missingDocument) {
      data.document = null;
    } else {
      data.reason = "";
    }
    Bill.cleanDocuments(data, removeUnsaved);
    return data;
  }

  // Formatting

  get project() {
    return this.projectID ? store.state.main.projects[this.projectID] : null;
  }

  get sale() {
    return this.saleID
      ? store.state.main.sales[this.projectID + this.saleID]
      : null;
  }

  get po() {
    return formatPO(this.projectID + this.saleID, this.data.orderPO);
  }

  get order() {
    return this.orderID ? store.state.main.orders[this.orderID] : null;
  }

  get ack() {
    return this.data.ackID ? store.state.main.acks[this.data.ackID] : null;
  }

  get invoice() {
    return this.data.invoiceID
      ? store.state.main.invoices[this.data.invoiceID]
      : null;
  }

  get vendor() {
    return this.data.vendorID
      ? store.state.main.companies[this.data.vendorID]
      : null;
  }

  get document() {
    return this.data.document ? new DocumentRef(this.data.document) : null;
  }

  get totalCost() {
    return this.data.totalCost !== null
      ? formatCurrency(
          this.data.creditMemo ? -this.data.totalCost : this.data.totalCost
        )
      : null;
  }

  get balance() {
    return formatCurrency(this.data.balance);
  }

  get totalPayments() {
    return this.data.totalCost !== null
      ? formatCurrency(this.data.totalCost - this.data.balance)
      : null;
  }

  get createdBy() {
    return this.data.createdBy && store.state?.main?.users
      ? store.state.main.users[this.data.createdBy]
      : null;
  }

  get modifiedBy() {
    return this.data.modifiedBy && store.state?.main?.users
      ? store.state.main.users[this.data.modifiedBy]
      : null;
  }

  get status() {
    if (this.isCreditMemo) {
      return "credit";
    } else if (Number(this.data.balance) <= 0) {
      return "paid";
    } else if (isDue(this.data.dueDate)) {
      return "due";
    } else if (isDue(this.data.dueDate, 7)) {
      return "near-due";
    } else {
      return "unpaid";
    }
  }

  get statusType() {
    const type = this.status;
    const info = BILL_STATUS_TYPES[this.status];
    return info ? { type, ...info, ...COLORS[info.color] } : null;
  }
}
